import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { DjinnLedger, DjinnWatchState } from "@magicweave/api";

import { useApi } from "../api/context";
import { unwrap } from "../api/shell-hooks";
import { djinnOff, useRetryUnlessOff } from "./hooks";

/** How often the page reads again while Djinn is working or looking, and while it is quiet. */
export const LEDGER_BUSY_MS = 2000;
export const LEDGER_QUIET_MS = 15_000;
/** The lamp's own read while the sheet is closed: slow, because nobody is looking at it. */
export const LEDGER_LAMP_CLOSED_MS = 60_000;

/** Under the Djinn prefix, so a save or a fix started in the sheet reaches it with the rest. */
export const ledgerKey = (projectId: string) =>
  ["project", projectId, "djinn", "ledger"] as const;

/**
 * What Djinn is doing and has done in a game: what waits on a person, what runs, the
 * watch, the last 30 days and the hit rate. The page passes `poll` and reads again every
 * 2 s while a fix runs or a check is under way, 15 s otherwise; the rail's dot reads
 * once and shares the answer.
 */
export function useDjinnLedger(
  projectId: string,
  {
    poll = false,
    every,
    enabled = true,
  }: { poll?: boolean; every?: number; enabled?: boolean } = {},
) {
  const api = useApi();
  const retry = useRetryUnlessOff();
  return useQuery({
    queryKey: ledgerKey(projectId),
    queryFn: async (): Promise<DjinnLedger> =>
      unwrap(
        await api.GET("/v1/projects/{projectId}/djinn/ledger", {
          params: { path: { projectId } },
        }),
      ),
    enabled: Boolean(projectId) && enabled,
    staleTime: 10_000,
    // A hidden tab reads nothing, and coming back to it reads at once.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: every === undefined ? true : "always",
    retry,
    refetchInterval: (query) => {
      // Off for the studio: the same answer comes back until staff change it.
      if (djinnOff(query.state.error) !== undefined) return false;
      if (every !== undefined) return every;
      if (!poll) return false;
      const data = query.state.data;
      return data && (data.running.length > 0 || data.watch.running === true)
        ? LEDGER_BUSY_MS
        : LEDGER_QUIET_MS;
    },
  });
}

/**
 * The ledger as the header's lamp reads it: every 60 s while the sheet is closed and the tab
 * is visible, every 15 s while the sheet is open, and again at once when the tab is focused.
 * A top-up, a resume or a staff pause shows without anyone opening the sheet.
 */
export function useDjinnLampLedger(projectId: string, { sheetOpen }: { sheetOpen: boolean }) {
  return useDjinnLedger(projectId, {
    every: sheetOpen ? LEDGER_QUIET_MS : LEDGER_LAMP_CLOSED_MS,
  });
}

function useWatchWrite<V>(projectId: string, write: (value: V) => Promise<DjinnWatchState>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: write,
    // The answer is the watch as it now stands: the times move at once. The read after it
    // is the truth, and the only way a refused press (inside 10 minutes) learns when.
    onSuccess: (watch) => {
      qc.setQueryData<DjinnLedger>(ledgerKey(projectId), (old) =>
        old ? { ...old, watch } : old,
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ledgerKey(projectId) }),
  });
}

/** Looks at the game at once. Free; any member who can read may press it. */
export function useCheckDjinnWatchNow(projectId: string) {
  const api = useApi();
  return useWatchWrite<void>(projectId, async () =>
    unwrap(
      await api.POST("/v1/projects/{projectId}/djinn/watch/check-now", {
        params: { path: { projectId } },
      }),
    ),
  );
}

/** Pauses or resumes the watch. A developer, an admin or an owner. */
export function usePutDjinnWatch(projectId: string) {
  const api = useApi();
  return useWatchWrite<boolean>(projectId, async (paused) =>
    unwrap(
      await api.PUT("/v1/projects/{projectId}/djinn/watch", {
        params: { path: { projectId } },
        body: { paused },
      }),
    ),
  );
}

/** Holds a finding back for 7 days, from the link in an email. Nothing is charged. */
export function useSnoozeDjinnFinding(projectId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (findingId: string): Promise<string> =>
      unwrap(
        await api.POST("/v1/projects/{projectId}/djinn/findings/{findingId}/snooze", {
          params: { path: { projectId, findingId } },
        }),
      ).snoozed_until,
    onSettled: () => qc.invalidateQueries({ queryKey: ledgerKey(projectId) }),
  });
}
